import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { chatRepository } from "../data/chat-repository";
import { useApprovalStatus } from "../../../hooks/useApprovalStatus";
import { usePublicChatDraft } from "../domain/public-chat-draft-store";
import { generateUUID } from "../../../utils/identity";
import type { ChatAttachmentInput, ChatInsight, ChatMessage } from "../data/types/chat";

const now = () => new Date().toISOString();

/** Message limit per chat for buyers without approved documents. */
export const TRIAL_MESSAGE_LIMIT = 3;

/** Trailing-write interval for the guest chat draft (see the sync effect). */
const DRAFT_PERSIST_MS = 400;

interface Turn {
  text: string;
  attachments?: ChatAttachmentInput[];
  productId?: number;
}

// A turn sent while the previous one is still streaming — its user bubble is
// already in the transcript (flagged `queued`), and it is dispatched
// automatically once the current turn settles (see runTurn's settle).
interface QueuedTurn extends Turn {
  bubble: ChatMessage;
}

function placeholderAssistant(chatId: string): ChatMessage {
  return { id: `assistant-${generateUUID()}`, chatId, role: "assistant", text: "", timestamp: now() };
}

function tempUser(chatId: string, text: string): ChatMessage {
  return { id: `user-${generateUUID()}`, chatId, role: "user", text, timestamp: now() };
}

/**
 * Puts `reply` right after the turn's user message. A queued message is
 * already in the transcript — it is un-flagged in place, so its reply lands
 * before any messages queued behind it; a fresh one is appended.
 */
function withTurn(prev: ChatMessage[], userMsg: ChatMessage, reply: ChatMessage): ChatMessage[] {
  const index = prev.findIndex((m) => m.id === userMsg.id);
  if (index < 0) return [...prev, userMsg, reply];
  return [...prev.slice(0, index), userMsg, reply, ...prev.slice(index + 1)];
}

function trialLimitMessage(chatId: string): ChatMessage {
  return {
    id: `assistant-trial-${generateUUID()}`,
    chatId,
    role: "assistant",
    text: `You've used your ${TRIAL_MESSAGE_LIMIT} trial messages. Your account is pending admin approval — once your documents are reviewed, you'll have unlimited access to the assistant.`,
    timestamp: now(),
  };
}

export interface UseChatResult {
  messages: ChatMessage[];
  responding: boolean;
  activeChatId: string | null;
  sendMessage: (text: string, attachments?: ChatAttachmentInput[], productId?: number) => Promise<void>;
  reset: () => void;
  loadChat: (chatId: string) => Promise<void>;
  /**
   * True for unapproved buyers — chat is in trial mode with a per-conversation
   * message cap (`TRIAL_MESSAGE_LIMIT`). False for sellers, admins, and
   * approved buyers (no cap).
   */
  isTrial: boolean;
  /** How many of the trial messages have been used so far in the active chat. */
  trialMessagesUsed: number;
  /** True once `trialMessagesUsed >= TRIAL_MESSAGE_LIMIT` and the user is on a trial. */
  trialQuotaReached: boolean;
  /** Re-sends the last failed message. Null when there is nothing to retry. */
  retryLastMessage: (() => void) | null;
  /** Aborts the in-flight streaming request. */
  stopGenerating: () => void;
}

export interface UseChatOptions {
  /** Pin a product into the BE catalog context for every message sent from this chat. */
  productId?: number | null;
  /**
   * The variant selected on the product page. Stock lives on the variant, so
   * without this the assistant quotes the template-wide total rather than the
   * figure the page displays.
   */
  variantId?: number | null;
  /**
   * Seed `messages`/`activeChatId` from the logged-out draft in
   * `sessionStorage` and keep it in sync, so an anonymous user's in-progress
   * search survives a detour through /login (e.g. clicking "Compare") and
   * back. Only the public landing page passes this — the authenticated
   * /ai-search page already restores via `chatId` in the URL + a backend
   * fetch, which doesn't apply to a logged-out user.
   */
  persistGuestDraft?: boolean;
}

export function useChat(options: UseChatOptions = {}): UseChatResult {
  const persistGuestDraft = options.persistGuestDraft ?? false;
  const [messages, setMessages] = useState<ChatMessage[]>(
    () => (persistGuestDraft ? usePublicChatDraft.getState().messages : []),
  );
  const [responding, setResponding] = useState(false);
  const [activeChatId, setActiveChatId] = useState<string | null>(
    () => (persistGuestDraft ? usePublicChatDraft.getState().activeChatId : null),
  );
  const [lastFailedInput, setLastFailedInput] = useState<string | null>(null);

  // Keep the draft in sync with every change, not just once a turn settles —
  // a widget (e.g. "Compare") embedded in the reply can become clickable,
  // and be clicked, before the trailing text finishes streaming.
  //
  // Throttled: `messages` changes on every streamed token, and setDraft goes
  // through zustand `persist`, which JSON-stringifies the whole transcript and
  // writes it to sessionStorage. Writing per token is O(n^2) main-thread work
  // and eventually throws QuotaExceededError out of the effect. A trailing
  // write every DRAFT_PERSIST_MS, plus a flush on unmount, keeps the draft
  // fresh enough for a mid-stream widget click without the cost.
  const draftRef = useRef({ messages, activeChatId });
  draftRef.current = { messages, activeChatId };
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushDraft = useCallback(() => {
    if (draftTimerRef.current) {
      clearTimeout(draftTimerRef.current);
      draftTimerRef.current = null;
    }
    try {
      usePublicChatDraft.getState().setDraft(draftRef.current);
    } catch {
      /* sessionStorage unavailable or full — the draft is a convenience */
    }
  }, []);

  useEffect(() => {
    if (!persistGuestDraft) return;
    if (draftTimerRef.current) return;
    draftTimerRef.current = setTimeout(() => {
      draftTimerRef.current = null;
      flushDraft();
    }, DRAFT_PERSIST_MS);
  }, [persistGuestDraft, messages, activeChatId, flushDraft]);

  useEffect(() => {
    if (!persistGuestDraft) return;
    return () => flushDraft();
  }, [persistGuestDraft, flushDraft]);

  const { status } = useApprovalStatus();
  const isTrial = status !== "approved";

  // Refs mirror state so sendMessage/loadChat can read fresh values
  // synchronously inside async callbacks (React state is one tick behind).
  const activeChatIdRef = useRef<string | null>(null);
  useEffect(() => { activeChatIdRef.current = activeChatId; }, [activeChatId]);

  // Queued messages are not sent yet, so they don't count — otherwise the
  // last allowed message would be blocked by its own queued bubble.
  const trialMessagesUsed = useMemo(
    () => messages.filter((m) => m.role === "user" && !m.queued).length,
    [messages],
  );
  const trialQuotaReached =
    isTrial && trialMessagesUsed >= TRIAL_MESSAGE_LIMIT;

  // Mirror gating decision via refs so sendMessage can stay stable across
  // renders without losing freshness.
  const isTrialRef = useRef(isTrial);
  useEffect(() => { isTrialRef.current = isTrial; }, [isTrial]);

  const productIdRef = useRef<number | null>(options.productId ?? null);
  useEffect(() => { productIdRef.current = options.productId ?? null; }, [options.productId]);
  const variantIdRef = useRef<number | null>(options.variantId ?? null);
  useEffect(() => { variantIdRef.current = options.variantId ?? null; }, [options.variantId]);
  const trialUsedRef = useRef(trialMessagesUsed);
  useEffect(() => { trialUsedRef.current = trialMessagesUsed; }, [trialMessagesUsed]);

  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => () => abortRef.current?.abort(), []);

  // Turns queued while a previous one is still streaming (see sendMessage).
  const queueRef = useRef<QueuedTurn[]>([]);
  // Mirrors `responding` so sendMessage can decide queue-vs-run synchronously.
  // Written together with the state (setRespondingNow) rather than synced in
  // an effect: between one turn settling and the next render a stale `true`
  // would queue a message that nothing is left to dispatch.
  const respondingRef = useRef(false);
  const setRespondingNow = useCallback((value: boolean) => {
    respondingRef.current = value;
    setResponding(value);
  }, []);

  const runTurnRef = useRef<((turn: Turn, queued?: ChatMessage) => Promise<void>) | undefined>(undefined);

  /** Runs one turn; `queued` is its already-displayed bubble when it waited in the queue. */
  const runTurn = useCallback(async (turn: Turn, queued?: ChatMessage) => {
    const { text, attachments, productId } = turn;
    const priorChatId = activeChatIdRef.current;
    const userMsg: ChatMessage = queued
      ? { ...queued, chatId: priorChatId ?? queued.chatId, queued: false }
      : tempUser(priorChatId ?? "", text);

    // Starts whatever was sent while this turn was still streaming, if any.
    const advanceQueue = () => {
      const next = queueRef.current.shift();
      if (!next) return;
      const { bubble, ...nextTurn } = next;
      void runTurnRef.current?.(nextTurn, bubble);
    };

    // Re-checked here, not just before queueing in sendMessage — a turn
    // queued while trial usage was still under the cap can find it reached
    // by the time its turn actually comes up.
    if (isTrialRef.current && trialUsedRef.current >= TRIAL_MESSAGE_LIMIT) {
      setMessages((prev) => withTurn(prev, userMsg, trialLimitMessage(priorChatId ?? "")));
      advanceQueue();
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const assistantMsg = placeholderAssistant(priorChatId ?? "");
    // Per-call override lets a single message (e.g. a "Chat about <product>"
    // click) pin a product without reconfiguring the whole hook instance —
    // falls back to the hook-level pin from `options.productId` otherwise.
    const effectiveProductId = productId ?? productIdRef.current;

    setMessages((prev) => withTurn(prev, userMsg, assistantMsg));
    setRespondingNow(true);

    // A stream can report both an error and a final `done` (the backend
    // persists the turn and closes it after an AI failure), or end without
    // `done` after an inline error. Only the first terminal callback counts —
    // a second one would start the next queued turn twice (aborting the
    // first) and clear the retry the error just offered.
    let settled = false;
    const settle = () => {
      if (settled) return false;
      settled = true;
      setRespondingNow(false);
      return true;
    };

    let acc = "";
    let finalInsights: ChatInsight[] | undefined;

    await chatRepository.sendMessageStream(
      text,
      priorChatId,
      attachments,
      {
        onChatId: (id) => {
          activeChatIdRef.current = id;
          setActiveChatId(id);
          setMessages((prev) => prev.map((m) =>
            m.id === userMsg.id || m.id === assistantMsg.id ? { ...m, chatId: id } : m,
          ));
        },
        onDelta: (delta) => {
          acc += delta;
          setMessages((prev) => prev.map((m) =>
            m.id === assistantMsg.id ? { ...m, text: acc } : m,
          ));
        },
        onDone: (insights) => {
          if (!settle()) return;
          finalInsights = insights;
          setLastFailedInput(null);
          setMessages((prev) => prev.map((m) => {
            if (m.id !== assistantMsg.id) return m;
            const text = m.text.trim()
              ? m.text
              : "Something went wrong generating a reply. Please try again.";
            return { ...m, text, insights: finalInsights };
          }));
          advanceQueue();
        },
        onError: (error) => {
          if (!settle()) return;
          setLastFailedInput(text);
          setMessages((prev) => prev.map((m) =>
            m.id === assistantMsg.id
              ? { ...m, text: m.text.trim() || `Sorry — ${error}` }
              : m,
          ));
          advanceQueue();
        },
      },
      controller.signal,
      effectiveProductId != null
        ? {
            productId: effectiveProductId,
            ...(variantIdRef.current != null ? { variantId: variantIdRef.current } : {}),
          }
        : undefined,
    );
  }, [setRespondingNow]);
  useEffect(() => { runTurnRef.current = runTurn; }, [runTurn]);

  const sendMessage = useCallback(async (text: string, attachments?: ChatAttachmentInput[], productId?: number) => {
    const trimmed = text.trim();
    if (!trimmed && !(attachments && attachments.length)) return;

    if (respondingRef.current) {
      // A reply is still streaming — show the message right away, flagged as
      // queued, and send it automatically once the reply finishes (see
      // runTurn's advanceQueue). Without the bubble the input just cleared
      // and nothing appeared, so users re-sent the same text, and every copy
      // was then sent on its own later.
      const bubble: ChatMessage = { ...tempUser(activeChatIdRef.current ?? "", trimmed), queued: true };
      setMessages((prev) => [...prev, bubble]);
      queueRef.current.push({ bubble, text: trimmed, attachments, productId });
      return;
    }

    await runTurn({ text: trimmed, attachments, productId });
  }, [runTurn]);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    activeChatIdRef.current = null;
    setActiveChatId(null);
    setMessages([]);
    setRespondingNow(false);
    setLastFailedInput(null);
    queueRef.current = [];
    if (persistGuestDraft) usePublicChatDraft.getState().clearDraft();
  }, [persistGuestDraft, setRespondingNow]);

  const retryLastMessage = useCallback(() => {
    if (!lastFailedInput) return;
    setLastFailedInput(null);
    sendMessage(lastFailedInput);
  }, [lastFailedInput, sendMessage]);

  const stopGenerating = useCallback(() => {
    abortRef.current?.abort();
    setRespondingNow(false);
    // An explicit stop cancels anything waiting behind the current turn too
    // — otherwise a queued message would fire right after the user asked
    // to stop. Its bubbles go with it, since they will never be sent.
    queueRef.current = [];
    setMessages((prev) => prev.filter((m) => !m.queued));
  }, [setRespondingNow]);

  const loadChat = useCallback(async (chatId: string) => {
    abortRef.current?.abort();
    activeChatIdRef.current = chatId;
    setActiveChatId(chatId);
    setRespondingNow(false);
    queueRef.current = [];
    const history = await chatRepository.getMessages(chatId);
    setMessages(history);
  }, [setRespondingNow]);

  return {
    messages,
    responding,
    activeChatId,
    sendMessage,
    reset,
    loadChat,
    isTrial,
    trialMessagesUsed,
    trialQuotaReached,
    retryLastMessage: lastFailedInput ? retryLastMessage : null,
    stopGenerating,
  };
}
